import type { ReactElement } from "react";
import { useCallback, useState } from "react";
import { AuthError, InteractionStatus } from "@azure/msal-browser";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { Button, Callout, Text } from "../ui-kit";
import styles from "./SignOutButton.module.css";

/**
 * The sign-out control (ISSUE-422). Visible exactly when a protected view
 * can render — `useIsAuthenticated()` is the same predicate `RequireAuth`
 * gates on, so this is also the fail-closed path when the runtime
 * configuration is incomplete: `AuthProvider` mounts no `MsalProvider` in
 * that case, `@azure/msal-react`'s default context reports `accounts: []`,
 * and `useIsAuthenticated()` reads `false`.
 */
export function SignOutButton(): ReactElement | null {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [signOutError, setSignOutError] = useState<string | null>(null);

  const handleSignOut = useCallback(() => {
    setSignOutError(null);
    setIsSigningOut(true);
    // No account argument — the load-bearing decision for this issue.
    // `RedirectClient.logout()` (installed `@azure/msal-browser` 5.20.0
    // source) branches `clearCacheOnLogout` on whether an account was
    // supplied: WITH one it calls `removeAccount(account)` (that account
    // only); WITH NONE it calls `browserStorage.clear()` + `clearKeystore()`
    // (every cached account and token). MSAL does not fall back to
    // `getActiveAccount()` for logout. Passing
    // `{ account: instance.getActiveAccount() }` — the common sample
    // pattern — would leave any OTHER cached account signed in, and this
    // app's auth gate (`useIsAuthenticated()`, true when `accounts.length >
    // 0`) plus `useAccessToken`'s fallback to a sole remaining cached
    // account would then read that operator as still signed in: a fail-open
    // sign-out (ISSUE-422 §Sign-out call shape).
    instance.logoutRedirect().catch((error: unknown) => {
      // `RedirectClient.logout()` can reject BEFORE any local cache is
      // cleared — an uninitialised instance, a redirect attempted from
      // inside an iframe (`BrowserUtils.redirectPreflightCheck`), or
      // `clearCacheOnLogout` itself throwing. A discarded rejection here
      // (the prior `void instance.logoutRedirect()`) left the operator
      // silently still signed in, on whatever authenticated screen they
      // were already on — `AuthProvider`'s sanitized error sink is only
      // rendered by `SignInView`, which never mounts in that state
      // (security-review finding, ISSUE-422). Sanitize the same way that
      // sink does: MSAL's short `errorCode` only, never the raw
      // `Error`/`AuthError` object or any token/claim content.
      setSignOutError(error instanceof AuthError ? error.errorCode : "unknown_error");
      // Reset our OWN progress flag rather than trusting MSAL's internal
      // interaction-in-progress guard to self-heal: `StandardController.
      // logoutRedirect` sets that guard with no `finally` (unlike
      // `logoutPopup`), so a pre-clear rejection can leave it — and the
      // `inProgress` state msal-react derives from it — stuck. Gating
      // `disabled` below on this local flag, not solely on `inProgress`,
      // keeps a failed attempt retryable without a page reload.
      setIsSigningOut(false);
    });
  }, [instance]);

  if (!isAuthenticated) {
    return null;
  }

  // Exclude `Logout` from the "blocked by another interaction" check: our
  // own in-flight attempt is already tracked by `isSigningOut`, and a stuck
  // `Logout` status left over from a prior failed attempt must not lock the
  // control forever (see the rejection handler above).
  const isBlockedByOtherInteraction =
    inProgress !== InteractionStatus.None && inProgress !== InteractionStatus.Logout;

  return (
    <span className={styles.wrapper}>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleSignOut}
        disabled={isSigningOut || isBlockedByOtherInteraction}
      >
        <span className={styles.label}>Sign out</span>
      </Button>
      {signOutError !== null ? (
        <Callout tone="danger" className={styles.error}>
          <Text variant="caption">
            Sign-out did not complete. Try again. (Reference: {signOutError})
          </Text>
        </Callout>
      ) : null}
    </span>
  );
}
